/* ActivityControl.jsx
   - Detailseite für eine Aktivität mit Editier-Drawer
   - Zeigt Status-Banner basierend auf Aktivitätsstatus
   - 2-Spalten-Layout mit Ortsbezeichnung/Terminen und Adresse
   - Hinweis-Banner mit Statusinformationen
*/
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { activitiesAPI } from '../services/api';
import { Check, Cross, InfoStroke, ErrorStroke } from '../components/Icons/CustomIcons';
import ActivityWheelchairIndicator from '../components/Common/ActivityWheelchairIndicator';
import { LocationBox } from '../components/Common/Card';
import { ActivityTopSection, ActivityCategoryList } from '../components/Grid/ActivityCommon';
import Loading from '../components/Common/Loading';
import EventScheduleDisplay from '../components/Common/EventScheduleDisplay';
import ActivityForm from '../components/Forms/ActivityForm';
import { AddressField } from './PagesCommon';
import { DescOutput } from '../components/Common/Dialog';
import CloseModalButton from '../components/Common/CloseModalButton';
import { MapsButton } from '../components/Common/ReferenceButtons';

const ActivityControl = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showEdit = searchParams.get('bearbeiten') === '1';
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const response = await activitiesAPI.getById(id);
        setActivity(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [id]);

  useEffect(() => {
    document.body.classList.toggle('drawer-open', showEdit);
    return () => document.body.classList.remove('drawer-open');
  }, [showEdit]);

  if (loading) return <Loading />;
  if (error) return <div>Error: {error}</div>;
  if (!activity) return <div>Activity not found</div>;

  const closeEdit = () => navigate(`/aktivitaet-kontrolle/${id}`, { replace: true });

  const mapsHref = activity.location_source === 'address'
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.address)}`
    : activity.latitude && activity.longitude
      ? `https://www.google.com/maps/search/?api=1&query=${activity.latitude},${activity.longitude}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.address)}`;

  const STATUS_BANNER = {
    active:   { text: 'Ist veröffentlicht und sichtbar',        bg: 'rgba(34, 197, 94, 0.15)',   border: '#22c55e', dot: '#22c55e' },
    pending:  { text: 'In Prüfung durch Moderation',            bg: 'rgba(240, 169, 37, 0.30)',  border: '#F0A925', dot: '#F0A925' },
    rejected: { text: 'Wurde von der Moderation abgelehnt',    bg: 'rgba(239, 68, 68, 0.15)',   border: '#ef4444', dot: '#ef4444' },
    archived: { text: 'Ist archiviert und nicht mehr sichtbar', bg: 'rgba(107, 114, 128, 0.15)', border: '#6b7280', dot: '#6b7280' },
  };
  const bannerConfig = STATUS_BANNER[activity.status];

  return (
    <>
      <div>
      {/* Edit-Drawer */}
      {showEdit && (
        <div
          className="drawer-overlay"
          onClick={closeEdit}
          role="dialog"
          aria-modal="true"
          aria-label="Aktivität bearbeiten"
        >
          <div
            className="drawer-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <CloseModalButton
              className="drawer-close"
              onClick={closeEdit}
              ariaLabel="Schliessen"
            >
              <Cross size={20} />
            </CloseModalButton>

            <ActivityForm
              mode="edit"
              initialData={activity}
              activityId={id}
              onSuccess={(updated) => {
                if (updated) setActivity(updated);
                closeEdit();
              }}
              onCancel={closeEdit}
            />
          </div>
        </div>
      )}
      </div>
      

      {/* Status-Banner */}
      {bannerConfig && (
        <div
          className="ctrl-status-banner"
          style={{ background: bannerConfig.bg, borderTopColor: bannerConfig.border, borderBottomColor: bannerConfig.border }}
        >
          <span className="ctrl-status-dot" style={{ background: bannerConfig.dot }} />
          <span className="ctrl-status-text">{bannerConfig.text}</span>
          {activity.rejection_reason && (
            <span className="ctrl-status-text" style={{ fontWeight: 400 }}>: «{activity.rejection_reason}»</span>
          )}
        </div>
      )}      
      <ActivityWheelchairIndicator wheelchairAccessible={activity.wheelchair_accessible} />
      
      <div className="activity-detail">
      <ActivityTopSection activity={activity} />
      
      <h2 className="line-height-default letter-spacing-tight">{activity.name}</h2>
      <DescOutput description={activity.description} />

      <ActivityCategoryList
        categories={activity.categories}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', margin: 'var(--spacing-4) 0' }}
        chipClassName="tag tag--category"
      />      

      {/* Hinweis-Banner */}
      {(() => {
        const HINT = {
          pending: {
            icon: <InfoStroke size={24} />,
            bg: 'var(--Color-White, #fff)', border: 'var(--Color-White, #fff)',
            title: 'Ihre Aktivität wird derzeit geprüft.',
            body: 'Sie ist noch nicht auf der Website sichtbar. Bitte haben Sie etwas Geduld – wir benachrichtigen Sie per E-Mail.',
          },
          active: {
            icon: <Check size={24} />,
            bg: 'var(--Color-White, #fff)', border: 'var(--Color-White, #fff)',
            title: 'Ihre Aktivität ist veröffentlicht und sichtbar.',
            body: null,
          },
          rejected: {
            icon: <ErrorStroke size={24} />,
            bg: '#fef2f2', border: '#ef4444',
            title: 'Ihre Aktivität konnte nicht veröffentlicht werden.',
            body: 'Sie entspricht leider nicht unseren Richtlinien. Bitte überprüfen Sie die Angaben und versuchen Sie es erneut.',
          },
          archived: {
            icon: <InfoStroke size={24} />,
            bg: 'var(--Color-White, #fff)', border: 'var(--Color-White, #fff)',
            title: 'Diese Aktivität ist archiviert und derzeit nicht sichtbar.',
            body: 'Sie können sie wiederherstellen. Nach einer erneuten Prüfung kann sie wieder veröffentlicht werden.',
          },
        };
        const hint = HINT[activity.status];
        if (!hint) return null;
        return (
          <div style={{
            background: hint.bg,
            border: `1px solid ${hint.border}`,
            borderRadius: '8px',
            borderTop: '1px solid var(--Colors-Grey-03, #DCE1E4)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            marginTop: 'var(--spacing-6)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--Spacings-Spacing-XXS)',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{hint.icon}</span>
            <div style={{ flex: 1 }}>
              <strong>{hint.title}</strong>
              {hint.body && (
                <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                  {hint.body}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {/* 2-Spalten-Layout (Desktop) */}
      <div className="activity-detail-columns">
      
        {/* Linke Spalte: Ortsbezeichnung, Termine */}
        <div className="activity-detail-col">
          <LocationBox locationName={activity.location_name} />

          <EventScheduleDisplay
            eventType={activity.event_type}
            openingHours={activity.opening_hours}
            eventSchedule={activity.event_schedule}
            accessibleFrom={activity.accessible_from}
            accessibleUntil={activity.accessible_until}
            website={activity.website}
            filterDate={null}
            activeTimeStart={null}
          />
        </div>

        {/* Rechte Spalte: Adresse */}
        <div className="activity-detail-col">
          <div className="anreise-header">         
            {activity.location_name && (
              <>
                <AddressField
                  address={activity.address}
                  headerActions={
                    <div className="activity-detail-address-actions activity-detail-actions-desktop">
                      <MapsButton href={mapsHref} />
                    </div>
                  }
                />
                {/* Mobile/Tablet: Buttons am Ende */}
                <div className="activity-detail-actions-mobile">
                  <MapsButton href={mapsHref} />
                </div>
              </>
            )}
        </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ActivityControl;
